import { logItems, type ItemAmount, type ItemValues, type LogLike } from './crimes-report';
import { dayKey, dayStart, inRange, sumPerDay, type DateRange } from './log-kit';

/**
 * Hunting, the dump and city finds from your own log (website roadmap S38 — torn.report's Hunting and Dump Search pages
 * and the City Finder log): per day and per item, valued at Torn's market value today. Pure and browser-safe; tested in
 * packages/shared/test/finds-report.test.ts.
 *
 * Torn doesn't document these lines' `data`: a line belongs to a kind by Torn's own category or title ("Hunting …",
 * "Dump search …"/"Search the dump", "City find …"/"Item found in the city"), items and money come from the usual field
 * names (or the one item a line names), what a hunt cost from cost/fee fields and items used. What can't be read is
 * counted, never guessed.
 */

export type FindKind = 'hunting' | 'dump' | 'city';

export interface FindKindInfo {
  id: FindKind;
  name: string;
  /** Torn's log category for it, by name (v2 torn/logcategories). */
  category: RegExp;
  blurb: string;
}

export const FIND_KINDS: readonly FindKindInfo[] = [
  { id: 'hunting', name: 'Hunting', category: /^hunt(ing)?$/i, blurb: 'Your hunts: what each day brought in, what it cost, the profit and its running total — and World Tiger Day.' },
  { id: 'dump', name: 'Dump', category: /^(the\s+)?dump(\s+search(es)?)?$|^search(ing)?\s+the\s+dump$/i, blurb: 'What searching the dump found: items per day and per item, at Torn’s market value today.' },
  { id: 'city', name: 'City finds', category: /^city(\s+find(s|er)?)?$|^item\s+finds?$/i, blurb: 'Items you found lying around the city: per day and per item, at Torn’s market value today.' },
];

export const findKindInfo = (id: FindKind): FindKindInfo => FIND_KINDS.find((k) => k.id === id)!;

type Obj = Record<string, unknown>;
const num = (x: unknown): number | null => {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x.replace(/[$,]/g, '')))) return Number(x.replace(/[$,]/g, ''));
  return null;
};
const firstOf = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
  return undefined;
};

/** Which kind a line is (null: none of these). */
export function findKindOf(l: Pick<LogLike, 'title' | 'category'>): FindKind | null {
  const cat = (l.category ?? '').trim().toLowerCase();
  const title = (l.title ?? '').trim().toLowerCase();
  if (/^hunt/.test(cat) || /^hunt(ing)?\b/.test(title)) return 'hunting';
  if (/\bdump\b/.test(cat) || /\bdump\b/.test(title)) return 'dump';
  if (/^city\b/.test(cat) || /\bcity\s*find|\bfound\b.*\bcity\b|\bcity\b.*\bfound\b|^item\s+finds?\b/.test(title) || /^item\s+finds?$/.test(cat)) return 'city';
  return null;
}

const GAINED = ['items_gained', 'item_gained', 'items_found', 'item_found', 'items_received', 'items', 'rewards', 'loot', 'pelts', 'trophies'];
const LOST = ['items_lost', 'item_lost', 'items_used', 'item_used', 'ammo_used'];
const MONEY = ['money_gained', 'money_found', 'money_earned', 'money', 'cash', 'cash_found', 'earned', 'income'];
const COST = ['cost', 'fee', 'money_spent', 'money_lost', 'price', 'session_cost'];
const SKILL = ['hunting_skill_increase', 'hunting_skill_increased', 'hunting_skill_gained', 'skill_increase', 'skill_increased', 'skill_gained', 'skill_gain'];

export interface FindLine {
  id: string;
  at: number;
  kind: FindKind;
  title: string;
  money: number;
  /** Money it cost (a hunt's fee), when the line says. */
  cost: number;
  gained: ItemAmount[];
  lost: ItemAmount[];
  /** Hunting skill the line gave, when it says. */
  skill: number | null;
  /** Energy used, when it says. */
  energy: number | null;
}

/** A log line → a hunt, a dump search or a city find (null: none of these). */
export function readFindLine(l: LogLike): FindLine | null {
  const kind = findKindOf(l);
  if (!kind) return null;
  const d = (l.data ?? {}) as Obj;
  let gained = logItems(firstOf(d, GAINED));
  const lost = logItems(firstOf(d, LOST));
  // A line that names one item (`item` + quantity): found, unless it was used.
  const one = num(d.item ?? d.item_id);
  if (one !== null && one > 0 && Number.isInteger(one) && !gained.some((x) => x.id === one) && !lost.some((x) => x.id === one)) {
    gained = [...gained, { id: one, amount: Math.max(1, num(firstOf(d, ['quantity', 'qty', 'amount'])) ?? 1) }];
  }
  let skill = num(firstOf(d, SKILL));
  if (skill === null) {
    const before = num(firstOf(d, ['hunting_skill_before', 'skill_before']));
    const after = num(firstOf(d, ['hunting_skill_after', 'skill_after']));
    if (before !== null && after !== null) skill = Math.round((after - before) * 1e6) / 1e6;
  }
  const energy = num(firstOf(d, ['energy_used', 'energy']));
  return {
    id: l.id,
    at: l.at,
    kind,
    title: (l.title ?? '').trim() || l.category || 'Not named',
    money: Math.max(0, num(firstOf(d, MONEY)) ?? 0),
    cost: Math.max(0, num(firstOf(d, COST)) ?? 0),
    gained,
    lost,
    skill: skill === null ? null : Math.max(0, skill),
    energy: energy === null ? null : Math.max(0, energy),
  };
}

export function readFindLog(lines: readonly LogLike[], kind?: FindKind): FindLine[] {
  return lines
    .map(readFindLine)
    .filter((l): l is FindLine => !!l && (!kind || l.kind === kind))
    .sort((a, b) => a.at - b.at);
}

// ---------------------------------------------------------------- value and profit

const worth = (items: readonly ItemAmount[], values: ItemValues) => items.reduce((a, i) => a + i.amount * (values.get(i.id) ?? 0), 0);
const unvalued = (items: readonly ItemAmount[], values: ItemValues) => items.reduce((a, i) => a + ((values.get(i.id) ?? null) === null ? i.amount : 0), 0);

/** What a line brought in (money + items at today's value). */
export const findWorth = (l: FindLine, values: ItemValues): number => l.money + worth(l.gained, values);
/** What it cost (money + items used at today's value). */
export const findCost = (l: FindLine, values: ItemValues): number => l.cost + worth(l.lost, values);
/** Profit: worth − cost. */
export const findProfit = (l: FindLine, values: ItemValues): number => findWorth(l, values) - findCost(l, values);

// ---------------------------------------------------------------- World Tiger Day

export interface TigerDay {
  /** Torn day start (ms). */
  day: number;
  /** Where it came from: Torn's calendar or the date (29 July). */
  from: 'calendar' | 'date';
}

/**
 * Is this moment on World Tiger Day? Torn's calendar (v2 torn/calendar) when it lists a "Tiger" event covering it; else
 * 29 July (Torn time), the day the world (and Torn) marks it.
 */
export function tigerDayOf(at: number, calendar: ReadonlyArray<{ title: string; start: number; end: number }> = []): TigerDay | null {
  const c = calendar.find((e) => /tiger/i.test(e.title) && at >= e.start && at <= e.end);
  if (c) return { day: dayStart(at), from: 'calendar' };
  const d = new Date(at);
  return d.getUTCMonth() === 6 && d.getUTCDate() === 29 ? { day: dayStart(at), from: 'date' } : null;
}

// ---------------------------------------------------------------- summing up

export interface FindTotals {
  lines: number;
  days: number;
  items: number;
  worth: number;
  money: number;
  cost: number;
  profit: number;
  /** Item units without a market value today (left out of worth, never guessed). */
  unvalued: number;
  skill: number;
  energy: number;
}

export function findTotals(lines: readonly FindLine[], r: DateRange, values: ItemValues): FindTotals {
  const shown = lines.filter((l) => inRange(l.at, r));
  const t: FindTotals = { lines: shown.length, days: new Set(shown.map((l) => dayStart(l.at))).size, items: 0, worth: 0, money: 0, cost: 0, profit: 0, unvalued: 0, skill: 0, energy: 0 };
  for (const l of shown) {
    t.items += l.gained.reduce((a, i) => a + i.amount, 0);
    t.money += l.money;
    t.worth += findWorth(l, values);
    t.cost += findCost(l, values);
    t.unvalued += unvalued(l.gained, values);
    t.skill += l.skill ?? 0;
    t.energy += l.energy ?? 0;
  }
  t.profit = t.worth - t.cost;
  t.skill = Math.round(t.skill * 1e4) / 1e4;
  return t;
}

export interface FindDayRow {
  day: number;
  key: string;
  lines: number;
  items: number;
  worth: number;
  cost: number;
  profit: number;
  /** Running profit from the range's first day up to and including this one. */
  cumulative: number;
  skill: number;
  tiger: TigerDay | null;
}

/** One row per day with any line (oldest first), with the running total. */
export function findDayRows(lines: readonly FindLine[], r: DateRange, values: ItemValues, calendar: ReadonlyArray<{ title: string; start: number; end: number }> = []): FindDayRow[] {
  const rows = new Map<number, FindDayRow>();
  for (const l of lines.filter((x) => inRange(x.at, r))) {
    const day = dayStart(l.at);
    let row = rows.get(day);
    if (!row) rows.set(day, (row = { day, key: dayKey(day), lines: 0, items: 0, worth: 0, cost: 0, profit: 0, cumulative: 0, skill: 0, tiger: tigerDayOf(l.at, calendar) }));
    row.lines++;
    row.items += l.gained.reduce((a, i) => a + i.amount, 0);
    row.worth += findWorth(l, values);
    row.cost += findCost(l, values);
    row.skill = Math.round((row.skill + (l.skill ?? 0)) * 1e4) / 1e4;
  }
  let run = 0;
  return [...rows.values()]
    .sort((a, b) => a.day - b.day)
    .map((row) => {
      row.profit = row.worth - row.cost;
      run += row.profit;
      row.cumulative = run;
      return row;
    });
}

export interface FindItemRow {
  id: number;
  found: number;
  used: number;
  each: number | null;
  worth: number | null;
  lines: number;
  last: number;
}

/** Items found (and used) in the range, most worth first. */
export function findItemRows(lines: readonly FindLine[], r: DateRange, values: ItemValues): FindItemRow[] {
  const rows = new Map<number, FindItemRow>();
  const row = (id: number) => {
    let x = rows.get(id);
    if (!x) rows.set(id, (x = { id, found: 0, used: 0, each: values.get(id) ?? null, worth: null, lines: 0, last: 0 }));
    return x;
  };
  for (const l of lines.filter((x) => inRange(x.at, r))) {
    for (const i of l.gained) {
      const x = row(i.id);
      x.found += i.amount;
      x.lines++;
      x.last = Math.max(x.last, l.at);
    }
    for (const i of l.lost) row(i.id).used += i.amount;
  }
  return [...rows.values()]
    .map((x) => ({ ...x, worth: x.each === null ? null : x.each * x.found }))
    .sort((a, b) => (b.worth ?? -1) - (a.worth ?? -1) || b.found - a.found);
}

export type FindMeasure = 'worth' | 'profit' | 'cumulative' | 'items' | 'lines';

/** Per Torn day: worth, profit, the running profit, items or lines. */
export function findsPerDay(lines: readonly FindLine[], measure: FindMeasure, r: DateRange, now: number, values: ItemValues): Array<{ at: number; v: number }> {
  if (measure === 'cumulative') {
    let run = 0;
    return sumPerDay(lines, (l) => findProfit(l, values), r, now).map((p) => ({ at: p.at, v: (run += p.v) }));
  }
  const f: Record<Exclude<FindMeasure, 'cumulative'>, (l: FindLine) => number> = {
    worth: (l) => findWorth(l, values),
    profit: (l) => findProfit(l, values),
    items: (l) => l.gained.reduce((a, i) => a + i.amount, 0),
    lines: () => 1,
  };
  return sumPerDay(lines, f[measure], r, now);
}

/** How the range's hunting on World Tiger Day compares with the other days. */
export function tigerDaySummary(days: readonly FindDayRow[]): { tigerDays: number; tigerProfit: number; tigerAvg: number | null; otherAvg: number | null } {
  const t = days.filter((d) => d.tiger);
  const o = days.filter((d) => !d.tiger);
  const avg = (xs: readonly FindDayRow[]) => (xs.length ? xs.reduce((a, d) => a + d.profit, 0) / xs.length : null);
  return { tigerDays: t.length, tigerProfit: t.reduce((a, d) => a + d.profit, 0), tigerAvg: avg(t), otherAvg: avg(o) };
}

export function findsCsvRows(lines: readonly FindLine[], values: ItemValues, name: (id: number) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  const items = (xs: readonly ItemAmount[]) => xs.map((i) => `${i.amount}x ${name(i.id)}`).join('; ');
  return {
    header: ['time_utc', 'kind', 'title', 'items_found', 'items_used', 'money', 'cost', 'worth_now', 'profit_now', 'skill_gained', 'energy'],
    rows: lines.map((l) => [new Date(l.at).toISOString(), l.kind, l.title, items(l.gained), items(l.lost), l.money, l.cost, findWorth(l, values), findProfit(l, values), l.skill, l.energy]),
  };
}

/** Personal stats' all-time finds (v2 `personalstats?cat=items`: found.city, found.dump). */
export function readFoundTotals(body: unknown): { city: number | null; dump: number | null } {
  const ps = (body && typeof body === 'object' ? (body as Obj).personalstats : null) as Obj | null;
  const items = (ps && typeof ps === 'object' ? ps.items : null) as Obj | null;
  const found = (items && typeof items === 'object' ? items.found : null) as Obj | null;
  return { city: num(found?.city), dump: num(found?.dump) };
}
